import M from 'materialize-css'
import { format } from 'date-fns'

import { useEvent, useState } from 'hooks'
import { dateHelper } from 'helper'

import { GlobalService } from '@/services'
import { constant } from './constant'
import { selectors } from './selector'

type PickDate = {
	from: Date
	to: Date
}
type SelectDate = {
	from: string
	to: string
}

type State = {
	isAjax?: boolean
	onSelect?: (date: string) => void
	onAction?: (date: SelectDate) => void
	selected?: {
		from: Date
		to: Date
	}
}

const DATE_FORMAT = 'yyyy-mm-dd'
const SET_DATE_FORMAT = 'yyyy-MM-dd'

const getDateByAction = (action: string): PickDate | null => {
	switch (action) {
		case 'this-year': {
			return { from: dateHelper.getFirstDayOfThisYear(), to: dateHelper.getLastDayOfThisYear() }
		}
		case 'last-year': {
			return { from: dateHelper.getFirstDayOfLastYear(), to: dateHelper.getLastDayOfLastYear() }
		}
		case 'this-month': {
			return { from: dateHelper.getFirstDayOfThisMonth(), to: dateHelper.getLastDayOfThisMonth() }
		}
		case 'last-quarter': {
			return { from: dateHelper.getFirstDayOfLastQuarter(), to: dateHelper.getLastDayOfLastQuarter() }
		}
		case 'this-quarter': {
			return { from: dateHelper.getFirstDayOfThisQuarter(), to: dateHelper.getLastDayOfThisQuarter() }
		}
		case 'last-month': {
			return { from: dateHelper.getFirstDayOfLastMonth(), to: dateHelper.getLastDayOfLastMonth() }
		}
		case 'this-week': {
			return { from: dateHelper.getFirstDayOfThisWeek(), to: dateHelper.getLastDayOfThisWeek() }
		}
		case 'last-week': {
			return { from: dateHelper.getFirstDayOfLastWeek(), to: dateHelper.getLastDayOfLastWeek() }
		}
		case 'today': {
			return { from: dateHelper.getToday(), to: dateHelper.getToday() }
		}
		case 'yesterday': {
			return { from: dateHelper.getYesterday(), to: dateHelper.getYesterday() }
		}
		case 'all': {
			return null
		}
		default: {
			return null
		}
	}
}

export const Datepicker = (() => {
	const { state, setState } = useState<State>({})
	const datepickers: M.Datepicker[] = []

	const getInstanceByEl = (el: HTMLElement) => {
		const index = datepickers.findIndex((item) => item.el === el)
		if (index < 0) return null

		return datepickers[index]
	}

	const handleAction = (scope: HTMLElement) => {
		const click = useEvent<Event>(scope, 'click')
		const action = scope.getAttribute(constant.selector.action)
		const parent = scope.closest<HTMLElement>(`.${constant.selector.target}`)
		if (!action || !parent) {
			return
		}

		const from = selectors.getInputFrom(parent)
		const to = selectors.getInputTo(parent)
		if (!from || !to) {
			return
		}

		const instanceFrom = getInstanceByEl(from)
		const instanceTo = getInstanceByEl(to)
		if (!instanceFrom || !instanceTo) {
			return
		}

		click.register(({ e }) => {
			e.preventDefault()

			const d = getDateByAction(action)
			if (!d) {
				from.value = ''
				to.value = ''
				if (state.onAction) state.onAction({ from: '', to: '' })
				return
			}

			instanceFrom.setDate(d.from)
			instanceTo.setDate(d.to)
			from.value = format(d.from, SET_DATE_FORMAT)
			to.value = format(d.to, SET_DATE_FORMAT)
			const newValue = { from: format(d.from, SET_DATE_FORMAT), to: format(d.to, SET_DATE_FORMAT) }
			const instanceFromEl = instanceFrom.el as HTMLInputElement
			const instanceToEl = instanceTo.el as HTMLInputElement
			instanceFromEl.setAttribute(constant.selector.fromValue, newValue.from)
			instanceToEl.setAttribute(constant.selector.toValue, newValue.to)

			setState({ selected: { from: d.from, to: d.to } })

			if (state.onAction) {
				state.onAction(newValue)
			}
		})
	}

	const initActions = () => {
		const actions = selectors.getActions()
		for (let i = 0, { length } = actions; i < length; i++) {
			handleAction(actions[i])
		}
	}

	const initInputs = () => {
		const inputs = selectors.getInputs()
		if (!inputs) return

		for (let i = 0, { length } = inputs; i < length; i++) {
			const input = inputs[i] as HTMLInputElement
			const fromValue = input.getAttribute(constant.selector.fromValue)
			const toValue = input.getAttribute(constant.selector.toValue)
			const isFrom = !!fromValue && fromValue !== '0'
			const isTo = !!toValue && toValue !== '0'
			const parent = input.closest<HTMLElement>(`.${constant.selector.target}`)
			if (!parent) return

			if (isFrom) {
				const f = format(new Date(fromValue || ''), SET_DATE_FORMAT)
				input.value = f
				input.setAttribute(constant.selector.fromValue, f)
			}

			if (isTo) {
				const f = format(new Date(toValue || ''), SET_DATE_FORMAT)
				input.value = f
				input.setAttribute(constant.selector.toValue, f)
			}

			const datepickerInstance = M.Datepicker.getInstance(input)
			const instance =
				datepickerInstance ||
				M.Datepicker.init(input, {
					firstDay: 1,
					i18n: {
						weekdays: dateHelper.getWeekdays(GlobalService.getLang()),
						weekdaysShort: dateHelper.getWeekdaysShorts(GlobalService.getLang()),
						weekdaysAbbrev: dateHelper.getWeekdaysMin(GlobalService.getLang()),
						months: dateHelper.getMonths(GlobalService.getLang()),
						monthsShort: dateHelper.getMonthsShorts(GlobalService.getLang()),
					},
					container: document.body,
					format: DATE_FORMAT,
					onSelect: (value) => {
						const isSelectedFrom = !!instance.el.getAttribute(constant.selector.fromValue)

						instance.el.setAttribute(
							isSelectedFrom ? constant.selector.fromValue : constant.selector.toValue,
							format(value, SET_DATE_FORMAT),
						)

						if (state.onSelect) {
							state.onSelect(format(value, SET_DATE_FORMAT))
						}

						setState({ selected: { ...state.selected, [isSelectedFrom ? 'from' : 'to']: value } })
					},
					onClose: () => {},
				})

			if (isFrom && state.selected) {
				instance.setDate(state.selected.from || new Date(fromValue!))
			}
			if (isTo && state.selected) {
				instance.setDate(state.selected.to || new Date(toValue!))
			}
			if (!datepickerInstance) {
				datepickers.push(instance)
			}
		}
	}

	const init = (props?: State) => {
		setState({ ...props })
		initInputs()
		initActions()
	}

	return {
		init,
	}
})()
